import { DESCRIPTION_CHARACTER_LENGTH } from 'constants/map';
import { useZoneContext, ZoneContext } from 'context/ZoneContext';
import { Station } from 'pages/Map/types';
import { useCallback, useContext, useRef } from 'react';
import { useOutsideAlerter } from 'utils/utils';
import { DescriptionContainer, DescriptionText, DescriptionWrapper, Ellipsis, StationCurrentLocationIcon } from './Description.styled';
import { getBackgroundColor, getFixedInterval, getTruncatedDescription } from './utils';
import useMapSearch from 'hooks/search/useMapSearch';
import { isTypeOfExpandedStation } from '../../utils';
import { MapTourContext } from 'context/MapTourContext';
import { TourType } from 'pages/Map/components/MapTour';

type DescriptionExpanded = Description & {
    height: number;
    backgroundColor: string;
    searchBackgroundColor: string | null;
    searchStatus: string | null;
};
const DescriptionExpanded = ({
    id,
    station,
    onDescriptionCollapse,
    onDescriptionExpand,
    isActiveStation,
    onClick,
    height,
    backgroundColor,
    searchBackgroundColor,
    searchStatus
}: DescriptionExpanded) => {
    const descriptionWrapperRef = useRef<HTMLInputElement>(null);
    const { scale } = useZoneContext();

    const onCollapse = useCallback(() => {
        onDescriptionCollapse?.();
    }, [onDescriptionCollapse]);

    useOutsideAlerter(descriptionWrapperRef, onCollapse);

    return (
        <DescriptionWrapper
            id={id}
            ref={descriptionWrapperRef}
            isActiveStation={isActiveStation}
            isExpand={true}
            className={searchStatus || ''}
            backgroundColor={backgroundColor}
            searchBackgroundColor={searchBackgroundColor}
        >
            <DescriptionContainer
                style={{ minHeight: `${height}px` }}
                scale={scale}
                data-testid="description-expanded"
                onMouseEnter={onDescriptionExpand}
                onMouseLeave={onCollapse}
            >
                <DescriptionText textVariant="h4">
                    {isActiveStation ? (
                        <StationCurrentLocationIcon
                            width={`max(${24 / scale}px, 48px)`}
                            height={`max(${24 / scale}px, 48px)`}
                            data-testid="active-station-icon"
                        />
                    ) : null}
                    <span data-testid="description-content" onClick={onClick}>
                        {station.getDescription()}
                    </span>
                </DescriptionText>
            </DescriptionContainer>
        </DescriptionWrapper>
    );
};

export type Description = {
    id?: string;
    station: Station;
    onDescriptionExpand?: () => void;
    onDescriptionCollapse?: () => void;
    isActiveStation: boolean;
    onClick: () => void;
};

export const Description = ({ id, station, onDescriptionCollapse, onDescriptionExpand, isActiveStation, onClick }: Description) => {
    const { expandedStation, sections, expandedDescriptionStation, scale } = useContext(ZoneContext);
    const isExpand = expandedDescriptionStation && expandedDescriptionStation.id === station.id;
    //reveals extra characters at fixed interval of zoomes
    const incrementalCharacterFactor = scale < 1.2 ? 5 : 15;
    const CHAR_LIMIT = DESCRIPTION_CHARACTER_LENGTH + getFixedInterval(scale) * incrementalCharacterFactor;
    const description = station.getDescription();
    const descriptionWrapperRef = useRef<HTMLInputElement>(null);
    const { getSearchColor, getSearchStatus } = useMapSearch();
    const { isCurrentTourItem } = useContext(MapTourContext);

    const searchStatus = getSearchStatus(station.id);
    const searchColor = getSearchColor(
        station.id,
        expandedStation?.id !== station.id && isTypeOfExpandedStation(station) ? station : undefined
    );

    const onEllipsesClick = useCallback(
        event => {
            event.stopPropagation();
            onDescriptionExpand?.();
        },
        [onDescriptionExpand]
    );

    const hasMoreDescription = description.length > CHAR_LIMIT;
    const sectionIndex = sections.findIndex(({ id }) => id === station.sectionID);
    const backgroundColor =
        !isExpand && isCurrentTourItem(TourType.STATION_AND_DRAWERS, station.id)
            ? ''
            : getBackgroundColor(station, expandedStation, true, sectionIndex);

    return (
        <>
            {isExpand ? (
                <DescriptionExpanded
                    id={id}
                    station={station}
                    isActiveStation={isActiveStation}
                    onClick={onClick}
                    onDescriptionCollapse={onDescriptionCollapse}
                    onDescriptionExpand={onDescriptionExpand}
                    height={descriptionWrapperRef.current?.clientHeight || 0}
                    backgroundColor={backgroundColor}
                    searchBackgroundColor={searchColor}
                    searchStatus={searchStatus}
                />
            ) : null}
            <DescriptionWrapper
                style={isExpand ? { visibility: 'hidden' } : {}}
                id={id}
                isActiveStation={isActiveStation}
                isExpand={false}
                backgroundColor={searchColor || backgroundColor}
                searchBackgroundColor={searchColor}
                className={searchStatus || ''}
            >
                <DescriptionContainer
                    ref={descriptionWrapperRef}
                    data-testid="description"
                    scale={scale}
                    onMouseEnter={onDescriptionExpand}
                    onMouseLeave={onDescriptionCollapse}
                >
                    <DescriptionText textVariant="h4">
                        {isActiveStation ? (
                            <StationCurrentLocationIcon
                                data-testid="active-station-icon"
                                width={`max(${24 / scale}px, 48px)`}
                                height={`max(${24 / scale}px, 48px)`}
                            />
                        ) : null}
                        <span data-testid="description-content" onClick={onClick}>
                            {hasMoreDescription ? getTruncatedDescription(description, CHAR_LIMIT) : description}
                        </span>
                        {hasMoreDescription && <Ellipsis data-testid="desc-ellipsis" onClick={onEllipsesClick} />}
                    </DescriptionText>
                </DescriptionContainer>
            </DescriptionWrapper>
        </>
    );
};

Description.displayName = 'Description';
